import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigation } from 'app/hooks/navigation';
import { useAuthorize } from 'app/hooks/use-authorize';
import { useUser } from 'app/network/user';
import { useTrackNewListingSource } from 'app/network/listing';
import { useNewListingPrompt } from 'app/screens/new/prompt';
import { NewListingPrompt as NewListingPromptSmall } from 'app/screens/new/prompt.small';

import { ListingType } from 'app/types/listing';
import type { PublishPromptType } from 'app/atoms/ui';
import { useOpenNewWithGuard } from 'app/hooks/use-open-new';
import { useCanUseServices } from 'app/network/category';
import { useUISize } from 'app/utils/web-helpers';
import { CreateListingBannerButton } from './create-listing-banner-button';

export const CreateRequestBanner = function CreateRequestBanner() {
  const { t } = useTranslation('all-listings');
  const navigation = useNavigation();
  const withAuthorize = useAuthorize();
  const { isSmallUI, isLargeUI } = useUISize();

  const { data: user } = useUser();
  const canUseDonations = Boolean(user?.canPublishDonations);
  const canUseServices = useCanUseServices();

  const trackNewListingSource = useTrackNewListingSource();
  const { showNewListingPrompt } = useNewListingPrompt();
  const openNewWithGuard = useOpenNewWithGuard();

  // Only used on large UI: NavigationBar already mounts a NewListingPrompt on small UI.

  const localPromptState = useState<boolean | PublishPromptType>(false);
  const [, setLocalPromptVisible] = localPromptState;

  const hasExtraOptions = canUseDonations || canUseServices;

  const createNeed = () =>
    withAuthorize(() => {
      if (isSmallUI) {
        if (hasExtraOptions) {
          // Drives the NewListingPrompt already mounted in NavigationBar.
          showNewListingPrompt('needs_list_top_banner', ListingType.request);
        } else {
          openNewWithGuard(navigation, {
            defaultValues: { type: ListingType.request },
            newListingSource: 'needs_list_top_banner',
          });
        }
      } else {
        if (hasExtraOptions) {
          // On large UI, NewListingPrompt is only mounted inside PublishButton when
          // isButtonPressed is true. Use a local bottom sheet instance instead.
          trackNewListingSource('needs_list_top_banner');
          setLocalPromptVisible(ListingType.request);
        } else {
          openNewWithGuard(navigation, {
            defaultValues: { type: ListingType.request },
            newListingSource: 'needs_list_top_banner',
          });
        }
      }
    });

  return (
    <>
      <CreateListingBannerButton label={t('createYourNeed')} onPress={createNeed} />
      {isLargeUI && hasExtraOptions ? <NewListingPromptSmall state={localPromptState} /> : null}
    </>
  );
};
